import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, User, FileText, CheckCircle, XCircle, AlertCircle, Edit, Eye } from "lucide-react";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { StageActionButtons } from "./StageActionButtons";
import { StageDetailDialog } from "./StageDetailDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
interface WorkflowStagesProps {
  candidateId: string;
  candidateName: string;
  currentStage: string;
  workflowData?: any;
  interviews?: any[];
  onStageAction: (action: string, data?: any) => void;
}
export function WorkflowStages({
  candidateId,
  candidateName,
  currentStage,
  workflowData = {},
  interviews = [],
  onStageAction
}: WorkflowStagesProps) {
  const {
    toast
  } = useToast();
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<any>(null);
  const [candidateData, setCandidateData] = useState<any>(null);
  const [workflowStages, setWorkflowStages] = useState<any[]>([]);
  const [actualCurrentStage, setActualCurrentStage] = useState(currentStage);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [hasGuardProfile, setHasGuardProfile] = useState(false);

  useEffect(() => {
    fetchWorkflowStages();
    fetchCandidateData();
  }, [candidateId, currentStage]);

  // Fetch workflow stages to determine actual current stage
  const fetchWorkflowStages = async () => {
    try {
      const { data, error } = await supabase
        .from('hr_workflow_stages')
        .select('*')
        .eq('candidate_id', candidateId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setWorkflowStages(data || []);
      
      // Determine actual current stage based on workflow stages
      if (data && data.length > 0) {
        const completedStages = data.filter(stage => stage.status === 'completed');
        const lastCompletedStage = completedStages[0];
        
        if (lastCompletedStage) {
          if (lastCompletedStage.stage_name === 'first_test') {
            setActualCurrentStage('second_test');
          } else if (lastCompletedStage.stage_name === 'second_test') {
            setActualCurrentStage('final_decision');
          } else {
            setActualCurrentStage(currentStage);
          }
        }
      }
    } catch (error: any) {
      console.error('Error fetching workflow stages:', error);
    }
  };

  // Fetch candidate data for detail views
  const fetchCandidateData = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('candidates').select('*').eq('id', candidateId).single();
      if (error) throw error;
      setCandidateData(data);
    } catch (error: any) {
      console.error('Error fetching candidate data:', error);
    }
  };

  // Determine if a guard profile exists for this candidate (by email)
  useEffect(() => {
    const checkGuard = async () => {
      try {
        if (!candidateData?.email) { setHasGuardProfile(false); return; }
        const { data } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', candidateData.email)
          .eq('Role', 'guard');
        setHasGuardProfile(!!data && data.length > 0);
      } catch (e) {
        setHasGuardProfile(false);
      }
    };
    checkGuard();
  }, [candidateData?.email]);

  // Check if a stage can be deleted (no subsequent stages completed)
  const canDeleteStage = (stageId: string): boolean => {
    const currentStageOrder = getStageOrder(stageId);
    const currentWorkflowOrder = getStageOrder(currentStage);

    // Can delete if current workflow stage order is <= the stage order + 1
    // This means the next stage hasn't been completed yet
    return currentWorkflowOrder <= currentStageOrder + 1;
  };

  // Check if stage actions should be shown (stage is completed but next stage is not)
  const shouldShowStageActions = (stageId: string): boolean => {
    const currentStageOrder = getStageOrder(stageId);
    const currentWorkflowOrder = getStageOrder(currentStage);

    // Show actions if stage is completed but the workflow hasn't progressed beyond next stage
    return currentWorkflowOrder <= currentStageOrder + 1;
  };

  // Handle stage actions
  const handleStageView = async (stage: any) => {
    // For evaluation stages, call specific view actions instead of opening dialog
    switch (stage.id) {
      case 'first_interview_evaluation':
      case 'first_interview_completed':
        const firstEvaluationForView = interviews.find(i => i.interview_type === 'first' && i.status === 'completed');
        if (firstEvaluationForView) {
          onStageAction('view_first_evaluation', firstEvaluationForView);
        }
        break;
      case 'second_interview':
        console.log('Viewing second interview, interviews:', interviews);
        const secondInterviewForView = interviews.find(i => i.interview_type === 'second' && i.status === 'completed');
        console.log('Found second interview:', secondInterviewForView);
        if (secondInterviewForView) {
          onStageAction('view_second_interview', secondInterviewForView);
        } else {
          console.warn('No completed second interview found');
        }
        break;
      case 'second_interview_evaluation':
        const secondEvaluationForView = interviews.find(i => i.interview_type === 'second' && i.status === 'completed');
        if (secondEvaluationForView) {
          onStageAction('view_second_evaluation', secondEvaluationForView);
        }
        break;
      case 'occupational_testing':
        onStageAction('view_test_results');
        break;
      case 'first_test':
        onStageAction('view_test_results_cognitive');
        break;
      case 'second_test':
        onStageAction('view_test_results_ipip_neo');
        break;
      default:
        await fetchCandidateData();
        setSelectedStage(stage);
        setDetailDialogOpen(true);
        break;
    }
  };
  const handleStageEdit = (stage: any) => {
    // Trigger the appropriate edit action based on stage type
    switch (stage.id) {
      case 'first_interview_scheduled':
        const firstScheduled = interviews.find(i => i.interview_type === 'first' && i.status === 'scheduled');
        if (firstScheduled) {
          onStageAction('reschedule_first_interview', firstScheduled);
        }
        break;
      case 'second_interview_scheduled':
        const secondScheduled = interviews.find(i => i.interview_type === 'second' && i.status === 'scheduled');
        if (secondScheduled) {
          onStageAction('reschedule_second_interview', secondScheduled);
        }
        break;
      case 'first_interview':
        const firstCompleted = interviews.find(i => i.interview_type === 'first' && i.status === 'completed');
        if (firstCompleted) {
          onStageAction('edit_first_interview', firstCompleted);
        }
        break;
      case 'second_interview':
        const secondCompleted = interviews.find(i => i.interview_type === 'second' && i.status === 'completed');
        if (secondCompleted) {
          onStageAction('edit_second_interview', secondCompleted);
        }
        break;
      case 'first_interview_evaluation':
      case 'first_interview_completed':
        const firstEvaluation = interviews.find(i => i.interview_type === 'first' && i.status === 'completed');
        if (firstEvaluation) {
          onStageAction('edit_first_evaluation', firstEvaluation);
        }
        break;
      case 'second_interview_evaluation':
        const secondEvaluation = interviews.find(i => i.interview_type === 'second' && i.status === 'completed');
        if (secondEvaluation) {
          onStageAction('edit_second_evaluation', secondEvaluation);
        }
        break;
    }
  };
  const handleStageDelete = async (stage: any) => {
    try {
      switch (stage.id) {
        case 'first_interview_scheduled':
        case 'second_interview_scheduled':
          const interviewType = stage.id.includes('first') ? 'first' : 'second';
          const interview = interviews.find(i => i.interview_type === interviewType);
          if (interview) {
            const {
              error
            } = await supabase.from('interviews').delete().eq('id', interview.id);
            if (error) throw error;
            toast({
              title: "Επιτυχία",
              description: `Το στάδιο "${stage.name}" διαγράφηκε επιτυχώς`
            });

            // Refresh the parent component
            onStageAction('refresh_data');
          }
          break;
        case 'first_interview':
        case 'second_interview':
          const completedInterviewType = stage.id.includes('first') ? 'first' : 'second';
          const completedInterview = interviews.find(i => i.interview_type === completedInterviewType && i.status === 'completed');
          if (completedInterview) {
            const {
              error
            } = await supabase.from('interviews').update({
              status: 'scheduled',
              completed_at: null,
              evaluation_notes: null,
              evaluation_score: null
            }).eq('id', completedInterview.id);
            if (error) throw error;
            toast({
              title: "Επιτυχία",
              description: `Τα δεδομένα του σταδίου "${stage.name}" διαγράφηκαν επιτυχώς`
            });
            onStageAction('refresh_data');
          }
          break;
        case 'first_interview_evaluation':
        case 'first_interview_completed':
        case 'second_interview_evaluation':
          // Reset the evaluation data but keep the interview completed
          const evaluationType = stage.id.includes('first') ? 'first' : 'second';
          const evaluationInterview = interviews.find(i => i.interview_type === evaluationType && i.status === 'completed');
          if (evaluationInterview) {
            const {
              error
            } = await supabase.from('interviews').update({
              evaluation_notes: null,
              evaluation_score: null,
              recommendation: null
            }).eq('id', evaluationInterview.id);
            if (error) throw error;

            // Also delete any candidate evaluations
            await supabase.from('candidate_evaluations').delete().eq('interview_id', evaluationInterview.id);
            toast({
              title: "Επιτυχία",
              description: `Η αξιολόγηση του σταδίου "${stage.name}" διαγράφηκε επιτυχώς`
            });
            onStageAction('refresh_data');
          }
          break;
      }
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  // Get actual workflow stages and merge with hardcoded template
  const getStagesWithWorkflowData = () => {
    const stageTemplate = [{
      id: 'application_review',
      name: 'Αίτηση',
      description: 'Υποβολή αίτησης και αρχική αξιολόγηση',
      icon: FileText,
      order: 0
    }, {
      id: 'first_interview',
      name: '1η Συνέντευξη',
      description: 'Διεξαγωγή πρώτης συνέντευξης',
      icon: User,
      order: 1
    }, {
      id: 'first_interview_evaluation',
      name: 'Αξιολόγηση 1ης Συνέντευξης',
      description: 'Αποφάσεις για συνέχεια ή απόρριψη',
      icon: CheckCircle,
      order: 2
    }, {
      id: 'second_interview',
      name: '2η Συνέντευξη',
      description: 'Διεξαγωγή δεύτερης συνέντευξης',
      icon: User,
      order: 3
    }, {
      id: 'occupational_testing',
      name: 'Γνωστική & Ψυχολογική Αξιολόγηση',
      description: 'Έκτιμηση γνωστικών ικανοτήτων και προσωπικότητας',
      icon: FileText,
      order: 4
    }, {
      id: 'final_decision',
      name: 'Τελική Απόφαση',
      description: 'Πρόσληψη ή απόρριψη υποψηφίου',
      icon: CheckCircle,
      order: 5
    }];

  // Merge with actual workflow data
    return stageTemplate.map((template, index) => {
      const workflowStage = workflowStages.find(ws => ws.stage_name === template.id);
      let status = workflowStage ? workflowStage.status : 'pending';
      
      // If no workflow stages exist yet, make the first stage available
      if (workflowStages.length === 0 && index === 0) {
        status = 'current';
      }
      
      return {
        ...template,
        status: status === 'in_progress' ? 'current' : status,
        workflowData: workflowStage
      };
    });
  };

  const stages = getStagesWithWorkflowData();
  function getStageOrder(stageId: string): number {
    const stageMap: Record<string, number> = {
      'application': 0,
      'first_interview_scheduled': 1,
      'first_interview': 2,
      'first_interview_completed': 3,
      'first_interview_evaluation': 3,
      'second_interview_scheduled': 4,
      'second_interview': 5,
      'second_interview_completed': 6,
      'first_test': 6,
      'first_test_completed': 7,
      'second_test': 7,
      'second_test_completed': 8,
      'final_decision': 8,
      'hired': 10,
      'rejected': 11
    };
    return stageMap[stageId] || 0;
  }
  function getStatusColor(status: string): string {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'current':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'available':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  }
  function getStatusIcon(status: string) {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'current':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'available':
        return <CheckCircle className="w-4 h-4 text-yellow-600" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  }
  function renderStageActions(stage: any) {
    // Helper to find if there's a scheduled interview for a specific type
    const hasScheduledInterview = (type: string) => {
      return interviews.some(interview => interview.interview_type === type && interview.status === 'scheduled');
    };
    const getScheduledInterview = (type: string) => {
      return interviews.find(interview => interview.interview_type === type && interview.status === 'scheduled');
    };
    const getCompletedInterview = (type: string) => {
      return interviews.find(interview => interview.interview_type === type && interview.status === 'completed');
    };

    // Special case for interview stages - show appropriate buttons based on interview status
    if (stage.id === 'first_interview') {
      const completedInterview = getCompletedInterview('first');
      const scheduledInterview = getScheduledInterview('first');
      if (completedInterview) {
        // Interview completed - show only edit button
        return <div className="space-y-2">
            
          </div>;
      } else if (scheduledInterview) {
        // Interview scheduled but not completed - show conduct button
        return <div className="space-y-2">
            <Button size="sm" onClick={() => onStageAction('conduct_first_interview', scheduledInterview)} className="w-full">
              <User className="w-4 h-4 mr-2" />
              Διεξαγωγή Συνέντευξης
            </Button>
          </div>;
      }
    }
    if (stage.id === 'second_interview') {
      const completedInterview = getCompletedInterview('second');
      const scheduledInterview = getScheduledInterview('second');
      const hasTestProgress = workflowStages.some(ws => ['first_test','second_test'].includes(ws.stage_name) && (ws.status === 'completed' || ws.status === 'in_progress'));
      
      if (completedInterview) {
        // 2η συνέντευξη ολοκληρώθηκε — δώσε δυνατότητα παράκαμψης τεστ ΜΟΝΟ αν δεν έχει ξεκινήσει/ολοκληρωθεί κανένα τεστ
        return (
          <div className="space-y-2">
            {!hasTestProgress && (
              <Button size="sm" onClick={() => onStageAction('skip_to_final_decision')} className="w-full bg-yellow-600 hover:bg-yellow-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                Παράκαμψη Τεστ • Μετάβαση στην Τελική Απόφαση
              </Button>
            )}
          </div>
        );
      } else if (scheduledInterview) {
        // Interview scheduled but not completed - show conduct button
        return <div className="space-y-2">
            <Button size="sm" onClick={() => onStageAction('conduct_second_interview', scheduledInterview)} className="w-full">
              <User className="w-4 h-4 mr-2" />
              Διεξαγωγή 2ης Συνέντευξης
            </Button>
          </div>;
      } else if (stage.status === 'available') {
        // Stage is available but no interview scheduled yet - show schedule button
        return <div className="space-y-2">
            <Button size="sm" onClick={() => onStageAction('schedule_second_interview')} className="w-full">
              <Calendar className="w-4 h-4 mr-2" />
              Προγραμματισμός 2ης Συνέντευξης
            </Button>
          </div>;
      }
    }

    // For all other stages, only show actions if current or available
    if (stage.status !== 'current' && stage.status !== 'available') return null;
    switch (stage.id) {
      case 'application_review':
        return <div className="space-y-2">
            <Button size="sm" onClick={() => onStageAction('schedule_first_interview')} className="w-full">
              <Calendar className="w-4 h-4 mr-2" />
              Προγραμματισμός 1ης Συνέντευξης
            </Button>
          </div>;
      case 'first_interview_scheduled':
        const hasFirstScheduled = hasScheduledInterview('first');
        return <div className="space-y-2">
            {!hasFirstScheduled ? <Button size="sm" onClick={() => onStageAction('schedule_first_interview')} className="w-full">
                <Calendar className="w-4 h-4 mr-2" />
                Προγραμματισμός Συνέντευξης
              </Button> : <Button size="sm" variant="outline" onClick={() => onStageAction('reschedule_first_interview', getScheduledInterview('first'))} className="w-full">
                <Edit className="w-4 h-4 mr-2" />
                Επαναπρογραμματισμός
              </Button>}
          </div>;
      case 'first_interview':
        const firstInterview = getScheduledInterview('first');
        return <div className="space-y-2">
            {firstInterview ? <Button size="sm" onClick={() => onStageAction('conduct_first_interview', firstInterview)} className="w-full">
                <User className="w-4 h-4 mr-2" />
                Διεξαγωγή Συνέντευξης
              </Button> : <Button size="sm" onClick={() => onStageAction('conduct_first_interview')} className="w-full">
                <User className="w-4 h-4 mr-2" />
                Διεξαγωγή Συνέντευξης
              </Button>}
          </div>;
      case 'first_interview_evaluation':
      case 'first_interview_completed':
        const firstCompletedInterview = getCompletedInterview('first');
        return <div className="space-y-2">
            {firstCompletedInterview && <Button size="sm" onClick={() => onStageAction('evaluate_first_interview', firstCompletedInterview)} className="w-full">
                <CheckCircle className="w-4 h-4 mr-2" />
                Αξιολόγηση Υποψηφίου
              </Button>}
            <Button size="sm" onClick={() => onStageAction('proceed_to_second')} className="w-full bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Συνέχεια σε 2η Συνέντευξη
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onStageAction('reject_after_first')} className="w-full">
              <XCircle className="w-4 h-4 mr-2" />
              Απόρριψη
            </Button>
          </div>;
      case 'second_interview_scheduled':
        const hasSecondScheduled = hasScheduledInterview('second');
        return <div className="space-y-2">
            {!hasSecondScheduled ? <Button size="sm" onClick={() => onStageAction('schedule_second_interview')} className="w-full">
                <Calendar className="w-4 h-4 mr-2" />
                Προγραμματισμός 2ης Συνέντευξης
              </Button> : <Button size="sm" variant="outline" onClick={() => onStageAction('reschedule_second_interview', getScheduledInterview('second'))} className="w-full">
                <Edit className="w-4 h-4 mr-2" />
                Επαναπρογραμματισμός
              </Button>}
          </div>;
      case 'second_interview':
        const secondInterview = getScheduledInterview('second');
        return <div className="space-y-2">
            {secondInterview ? <Button size="sm" onClick={() => onStageAction('conduct_second_interview', secondInterview)} className="w-full">
                <User className="w-4 h-4 mr-2" />
                Διεξαγωγή 2ης Συνέντευξης
              </Button> : <Button size="sm" onClick={() => onStageAction('conduct_second_interview')} className="w-full">
                <User className="w-4 h-4 mr-2" />
                Διεξαγωγή 2ης Συνέντευξης
              </Button>}
          </div>;
      case 'occupational_testing':
        return <div className="space-y-2">
            <Button size="sm" onClick={() => onStageAction('start_first_test')} className="w-full bg-green-600 hover:bg-green-700">
              <FileText className="w-4 h-4 mr-2" />
              Έναρξη Γνωστικής Αξιολόγησης
            </Button>
            <Button size="sm" onClick={() => onStageAction('start_second_test')} className="w-full bg-blue-600 hover:bg-blue-700">
              <FileText className="w-4 h-4 mr-2" />
              Έναρξη IPIP-NEO Τεστ
            </Button>
            <Button size="sm" variant="outline" onClick={() => onStageAction('view_test_results')} className="w-full">
              Προβολή Αποτελεσμάτων
            </Button>
          </div>;
      case 'second_interview_completed':
      case 'first_test':
        return <div className="space-y-2">
            <Button size="sm" onClick={() => onStageAction('start_first_test')} className="w-full bg-green-600 hover:bg-green-700">
              <FileText className="w-4 h-4 mr-2" />
              Έναρξη Γνωστικής Αξιολόγησης
            </Button>
          </div>;
      case 'first_test_completed':
      case 'second_test':
        return <div className="space-y-2">
            <Button size="sm" onClick={() => onStageAction('start_second_test')} className="w-full bg-green-600 hover:bg-green-700">
              <FileText className="w-4 h-4 mr-2" />
              Έναρξη IPIP-NEO Τεστ
            </Button>
            <Button size="sm" variant="outline" onClick={() => onStageAction('view_test_results')} className="w-full">
              Προβολή Αποτελεσμάτων
            </Button>
          </div>;
      case 'second_test_completed':
      case 'final_decision':
        // Check if final_decision stage is completed
        const finalDecisionStage = workflowStages.find(stage => stage.stage_name === 'final_decision');
        const isCompleted = finalDecisionStage?.status === 'completed';
        const secondTestCompleted = workflowStages.some(stage => stage.stage_name === 'second_test' && stage.status === 'completed');
        const skippedToFinal = !secondTestCompleted && finalDecisionStage?.status === 'in_progress';
        
        if (isCompleted) {
          return <div className="space-y-2">
              <Button size="sm" className="w-full bg-green-600 hover:bg-green-700" disabled>
                <CheckCircle className="w-4 h-4 mr-2" />
                Onboarded
              </Button>
              <Button size="sm" variant="outline" onClick={() => onStageAction('view_test_results')} className="w-full">
                Προβολή Αποτελεσμάτων
              </Button>
            </div>;
        }
        
        const isHired = currentStage === 'hired';
        const showHire = !skippedToFinal && !isHired;
        const showReject = !isHired;
        const showMarkHired = isHired && hasGuardProfile && !isCompleted;
        
        return <div className="space-y-2">
            {showHire && (
              <Button size="sm" onClick={() => onStageAction('hire_candidate')} className="w-full bg-green-600 hover:bg-green-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                Πρόσληψη
              </Button>
            )}
            {showMarkHired && (
              <Button size="sm" onClick={() => onStageAction('mark_hired')} className="w-full bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                Σήμανση ως Προσληφθείς
              </Button>
            )}
            {showReject && (
              <Button size="sm" variant="destructive" onClick={() => setRejectDialogOpen(true)} className="w-full">
                <XCircle className="w-4 h-4 mr-2" />
                Τελική Απόρριψη
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onStageAction('view_test_results')} className="w-full">
              Προβολή Αποτελεσμάτων
            </Button>
          </div>;
      default:
        return null;
    }
  }
  return <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Διαδικασία Πρόσληψης</h3>
          <p className="text-sm text-muted-foreground">Υποψήφιος: {candidateName}</p>
        </div>
        <Badge variant="outline" className="text-sm">
          Στάδιο: {stages.find(s => s.id === actualCurrentStage)?.name || actualCurrentStage}
        </Badge>
      </div>

      <div className="grid gap-4">
        {stages.map((stage, index) => {
        const Icon = stage.icon;
        return <Card key={stage.id} className={`transition-all duration-200 ${stage.status === 'current' ? 'ring-2 ring-blue-500 shadow-md' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-full ${stage.status === 'completed' ? 'bg-green-100' : stage.status === 'current' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-4 h-4 ${stage.status === 'completed' ? 'text-green-600' : stage.status === 'current' ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{stage.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{stage.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(stage.status)}
                    {/* Show test results button for completed test stages */}
                    {(stage.id === 'occupational_testing' || stage.id === 'first_test' || stage.id === 'second_test') && stage.status === 'completed' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleStageView(stage)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        Αποτελέσματα
                      </Button>
                    )}
                    <Badge className={getStatusColor(stage.status)}>
                      {stage.status === 'completed' ? 'Ολοκληρώθηκε' : 
                       stage.status === 'current' ? 'Τρέχον' : 
                       stage.status === 'available' ? 'Διαθέσιμο' : 'Εκκρεμεί'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              {(stage.status === 'current' || stage.status === 'available' || renderStageActions(stage)) && <CardContent className="pt-0">
                  {renderStageActions(stage)}
                </CardContent>}

              {/* Show additional info for completed stages */}
              {stage.status === 'completed' && stage.workflowData && <CardContent className="pt-0">
                  <div className="text-sm text-muted-foreground">
                    {stage.workflowData.completed_at && <p>Ολοκληρώθηκε: {format(new Date(stage.workflowData.completed_at), 'dd/MM/yyyy HH:mm', {
                  locale: el
                })}</p>}
                    {stage.workflowData.notes && <p>Σημείωση: {stage.workflowData.notes}</p>}
                  </div>
                </CardContent>}

              {/* Action buttons for completed stages */}
              {stage.status === 'completed' && <CardContent className="pt-0">
                  <StageActionButtons stage={stage} canDelete={canDeleteStage(stage.id)} onView={() => handleStageView(stage)} onEdit={() => handleStageEdit(stage)} onDelete={() => handleStageDelete(stage)} />
                </CardContent>}
            </Card>;
      })}
      </div>

      {/* Detail Dialog */}
      <StageDetailDialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen} stage={selectedStage} data={{
      ...candidateData,
      interviews
    }} candidateName={candidateName} />

      {/* Reject Reason Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Αιτιολογία Απόρριψης</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              placeholder="Πληκτρολογήστε την αιτιολογία..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Ακύρωση</Button>
            <Button
              variant="destructive"
              onClick={() => {
                onStageAction('reject_candidate', { reason: rejectReason });
                setRejectDialogOpen(false);
                setRejectReason('');
              }}
              disabled={!rejectReason.trim()}
            >
              Επιβεβαίωση Απόρριψης
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}